import { Stack } from '@mui/material'
import { t } from 'i18next'
import { useEffect, useRef, useState } from 'react'

import Filter from '../../Layout/Dialogs/Filter.jsx'
import Count from '../../Shared/Components/Count.jsx'
import { useAuth } from '../../Shared/Contexts/AuthContext.jsx'
import { AreaFilter, OrganizationsFilter } from '../../Shared/Filters/Fields/index.js'
import FilterOthers from './OthersFilter.jsx'

const DevicesFilters = ({ filters = {}, itemsCount, onChange }) => {
  const [selectedArea, setSelectedArea] = useState(false)
  const [selectedOrganizations, setSelectedOrganizations] = useState(false)
  const [selectedOther, setSelectedOther] = useState(false)
  const [announcementUpdatePage, setAnnouncementUpdatePage] = useState('')
  const shouldAnnounceUpdatePage = useRef(false)
  const previousItemsCount = useRef(itemsCount)

  const { authState } = useAuth()

  const handleAreaFilterChanged = (data) => {
    onChange({
      ...filters,
      ...data
    })
  }

  const handleOrganizationsFilterChanged = (data) => {
    setSelectedOrganizations(true)
    onChange({
      ...filters,
      ...data
    })
  }

  const handleOtherFiltersChanged = (data) => {
    setSelectedOther(true)
    onChange({
      ...filters,
      ...data
    })
  }

  const handleOnDeleteArea = () => {
    setSelectedArea(false)
    onChange({
      ...filters,
      nearby: null
    })
    shouldAnnounceUpdatePage.current = true
  }

  const handleOnDeleteOrganizations = () => {
    setSelectedOrganizations(false)
    onChange({
      ...filters,
      organizationKeys: [],
      searchInSubOrganizations: false
    })
    shouldAnnounceUpdatePage.current = true
  }

  const handleOnDeleteOther = () => {
    setSelectedOther(false)
    onChange({
      ...filters,
      modelIds: [],
      showPublic: null,
      showOffline: null,
      showHidden: null,
      brandNamesEncoded: [],
      tags: [],
      tagSearchMode: null
    })
    shouldAnnounceUpdatePage.current = true
  }

  const handleFilterClose = () => {
    shouldAnnounceUpdatePage.current = true
    announceUpdatePage()
  }

  const getAnnouncementMessage = () => {
    if (itemsCount === 0) {
      return t('filters.accessibility.noItemsFound')
    } else if (itemsCount === 1) {
      return t('filters.accessibility.oneItemFound')
    } else {
      return t('filters.accessibility.itemsFound', { count: itemsCount })
    }
  }

  const announceUpdatePage = () => {
    setAnnouncementUpdatePage('')

    setTimeout(() => {
      setAnnouncementUpdatePage(getAnnouncementMessage())
    }, 100)
  }

  useEffect(() => {
    if (shouldAnnounceUpdatePage.current && previousItemsCount.current !== itemsCount) {
      announceUpdatePage()
      shouldAnnounceUpdatePage.current = false
    }
    previousItemsCount.current = itemsCount
  }, [itemsCount])

  useEffect(() => {
    const { brandNamesEncoded, modelIds, nearby, organizationKeys, showHidden, showOffline, showPublic, tagSearchMode, tags } = filters

    setSelectedArea(!!nearby)

    setSelectedOrganizations(organizationKeys?.length)

    setSelectedOther(
      modelIds?.length > 0 ||
      showOffline !== null ||
      showHidden !== null ||
      showPublic !== null ||
      brandNamesEncoded?.length > 0 ||
      tags?.length > 0 ||
      tagSearchMode !== null
    )
  }, [filters])

  const filterUnits = [
    {
      label: t('filters.devices.area.label'),
      id: 'chip-filters-devices-area',
      selected: selectedArea,
      onDelete: handleOnDeleteArea,
      onClose: handleFilterClose,
      content: (
        <AreaFilter
          filters={filters}
          onChange={handleAreaFilterChanged}
        />
      ),
      count: <Count itemsCount={itemsCount} />
    },
    {
      label: t('filters.devices.organizations.label'),
      id: 'chip-filters-devices-organizations',
      selected: selectedOrganizations,
      onDelete: handleOnDeleteOrganizations,
      onClose: handleFilterClose,
      hidden: !authState.isAuthenticated,
      content: (
        <OrganizationsFilter
          filters={filters}
          onChange={handleOrganizationsFilterChanged}
        />
      ),
      count: <Count itemsCount={itemsCount} />
    },
    {
      label: t('common.filters.other'),
      id: 'chip-filters-devices-other',
      selected: selectedOther,
      onDelete: handleOnDeleteOther,
      onClose: handleFilterClose,
      content: (
        <FilterOthers
          filters={filters}
          onChange={handleOtherFiltersChanged}
        />
      ),
      count: <Count itemsCount={itemsCount} />
    }
  ]

  return (
    <>
      {/* Live region per annunci screen reader */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: 'absolute',
          left: '-10000px',
          width: '1px',
          height: '1px',
          overflow: 'hidden'
        }}
      >
        {announcementUpdatePage}
      </div>
      <Stack
        direction="row"
        justifyContent="flex-start"
        alignItems="center"
        spacing={2}>
        {[...filterUnits].map((e, i) => (
          <Filter
            key={`device-filter-${i}`}
            id={e.id}
            onClick={e.onClick}
            onDelete={e.onDelete}
            onClose={e.onClose}
            selected={e.selected}
            title={e.label}
            hidden={e.hidden || false}
            content={e.content}
            count={e.count}
          />
        ))}
      </Stack>
    </>
  )
}

export default DevicesFilters